import React from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { BaseView, BaseText } from '@/components/base'
import { Upload } from '@/assets/icons'
import {
  GetMessageProps,
  useGetMessageShareRequest,
} from './hooks/useGetMessageShareRequest'

interface ShareRequestProps extends GetMessageProps {
  className?: string
  userId: string | undefined
}

export const ShareRequest: React.FC<ShareRequestProps> = ({
  className = '',
  userId,
  ...params
}) => {
  const { t } = useTranslation()
  const { isLoading, generateLink, getMessageWithLink } =
    useGetMessageShareRequest({
      ...params,
      userId: userId,
    })

  const handleShare = async () => {
    // Don't share if already loading
    if (isLoading) {
      return
    }

    try {
      // Generate link first (will use cached link if already exists)
      const generatedLink = await generateLink()

      // Get message with the freshly generated link
      const finalMessage = getMessageWithLink(generatedLink)

      if (navigator.share) {
        await navigator.share({
          title: 'Money Request',
          text: finalMessage,
        })
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(finalMessage)
        toast.success('Link copied to clipboard!')
      }
    } catch (error) {
      // Ignore share/clipboard errors but still allow retry
    }
  }

  return (
    <BaseView
      data-component="share-request"
      row
      align="center"
      gap={12}
      bgColor="background.cardItemRoot"
      rounded={24}
      className={`cursor-pointer hover:opacity-90 active:scale-[0.99] transition-all duration-150 ease-out ${className}`}
      onClick={handleShare}
      style={{
        padding: '16px', // Как в RN CardItem.Root
        paddingRight: '10px',
        minHeight: '76px', // Стандартная высота карточки как в RN
      }}
    >
      {/* Avatar with Gradient - увеличиваем для лучших пропорций */}
      <BaseView
        center
        circle
        circleSize={56} // Увеличиваем аватар
        style={{
          background: 'linear-gradient(90deg, #3C50FF 0%, #797FFF 100%)', // Точные цвета из RN
          flexShrink: 0,
        }}
      >
        <Upload size={32} color="#FFFFFF" /> {/* Пропорциональная иконка */}
      </BaseView>

      {/* Content - точно как CardItem.Content */}
      <BaseView
        flex={1}
        style={{
          paddingRight: '64px', // Как в RN: responsiveAdapter(64)
          justifyContent: 'center',
        }}
      >
        <BaseText
          fontSize={14} // Увеличено до размера CardItem.Title
          fontFamily="medium" // Как в RN CardItem.Title
          color="label.description"
          style={{
            lineHeight: '16px',
            margin: 0,
          }}
        >
          {t('translation:selectContacts.shareRequest')}
        </BaseText>
      </BaseView>

      {/* Right Icon - стандартный размер как в других компонентах */}
      <BaseView
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          width: '24px', // Стандартный размер контейнера
          height: '24px', // Стандартный размер контейнера
        }}
      >
        {/* Используем точно такой же SVG как в ContactItem */}
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{
            color: 'rgba(255, 255, 255, 0.6)',
            flexShrink: 0,
          }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </BaseView>
    </BaseView>
  )
}

export default ShareRequest
